import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

type SectionKey =
  | "introduction"
  | "quickstart"
  | "messaging"
  | "calls"
  | "groups"
  | "encryption"
  | "sdk"
  | "deployment"
  | "api"
  | "sfu";

// ---- Sub-components (defined before page so they can be used in content) ----

function CodeBlock({ children, label, copyLabel, copiedLabel }: {
  children: string;
  label: string;
  copyLabel: string;
  copiedLabel: string;
}) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="rounded-xl overflow-hidden border" style={{ borderColor: "var(--border-color)" }}>
      {label && (
        <div className="flex items-center justify-between px-4 py-2 border-b" style={{ borderColor: "var(--border-color)", backgroundColor: "var(--card-bg)" }}>
          <span className="text-xs font-medium" style={{ color: "var(--text-secondary)" }}>{label}</span>
          <button onClick={copy} className="flex items-center gap-1 text-xs cursor-pointer transition-colors" style={{ color: copied ? "var(--accent)" : "var(--text-secondary)" }}>
            <i className={copied ? "ri-check-line" : "ri-file-copy-line"}></i>
            {copied ? copiedLabel : copyLabel}
          </button>
        </div>
      )}
      <pre className="text-xs leading-relaxed p-4 overflow-x-auto" style={{ backgroundColor: "var(--code-bg)", color: "var(--code-text)" }}>
        <code>{children}</code>
      </pre>
    </div>
  );
}

function StepBlock({ step, title, children }: { step: number; title: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-4">
      <div className="flex flex-col items-center">
        <div className="w-8 h-8 flex items-center justify-center rounded-full text-sm font-bold shrink-0" style={{ backgroundColor: "var(--accent)", color: "#fff" }}>
          {step}
        </div>
        <div className="w-px flex-1 mt-2" style={{ backgroundColor: "var(--border-color)" }}></div>
      </div>
      <div className="pb-6 flex-1 min-w-0">
        <div className="font-semibold text-sm mb-3 pt-1" style={{ color: "var(--text-primary)" }}>{title}</div>
        {children}
      </div>
    </div>
  );
}

function FeatureList({ title, items }: { title: string; items: { icon: string; title: string; desc: string }[] }) {
  return (
    <div>
      <h2 className="text-xl font-bold mb-4" style={{ color: "var(--text-primary)" }}>{title}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((item) => (
          <div key={item.title} className="flex gap-3 p-4 rounded-xl border" style={{ borderColor: "var(--border-color)", backgroundColor: "var(--card-bg)" }}>
            <div className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0" style={{ backgroundColor: "var(--accent-light)" }}>
              <i className={`${item.icon} text-sm`} style={{ color: "var(--accent)" }}></i>
            </div>
            <div>
              <div className="font-semibold text-xs mb-0.5" style={{ color: "var(--text-primary)" }}>{item.title}</div>
              <div className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }} dangerouslySetInnerHTML={{ __html: item.desc }}></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---- Section content components (use hooks, so they must be real components) ----

function IntroductionSection() {
  const { t } = useTranslation();
  const cards = [
    { icon: "ri-open-source-line", title: t("feat.private.title"), desc: t("api.open.desc") },
    { icon: "ri-server-line", title: t("feat.private.title"), desc: t("api.deploy.desc") },
    { icon: "ri-speed-line", title: t("feat.selfhost.title"), desc: t("feat.selfhost.desc") },
  ];
  const abilities = [
    { icon: "ri-chat-1-line", title: t("feat.msg.title"), desc: t("feat.msg.desc") },
    { icon: "ri-video-line", title: t("feat.call.title"), desc: t("feat.call.desc") },
    { icon: "ri-group-line", title: t("feat.group.title"), desc: t("feat.group.desc") },
    { icon: "ri-shield-keyhole-line", title: t("feat.encrypt.title"), desc: t("feat.encrypt.desc") },
    { icon: "ri-code-s-slash-line", title: t("features.sdk.title"), desc: t("features.sdk.desc") },
    { icon: "ri-cloud-line", title: t("api.deploy.title"), desc: t("api.deploy.desc") },
  ];
  const platforms = ["Android", "iOS", "Web", "Windows", "macOS", "Linux"];
  return (
    <div className="space-y-8">
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4" style={{ backgroundColor: "var(--accent-light)", color: "var(--accent)" }}>
          v2.1.0
        </div>
        <h1 className="text-3xl font-bold mb-4" style={{ color: "var(--text-primary)" }}>
          TupIM — {t("hero.badge")}
        </h1>
        <p className="text-base leading-relaxed" style={{ color: "var(--text-secondary)" }}>{t("hero.desc")}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {cards.map((item, i) => (
          <div key={i} className="p-4 rounded-xl border" style={{ borderColor: "var(--border-color)", backgroundColor: "var(--card-bg)" }}>
            <div className="w-9 h-9 flex items-center justify-center rounded-lg mb-3" style={{ backgroundColor: "var(--accent-light)" }}>
              <i className={`${item.icon} text-lg`} style={{ color: "var(--accent)" }}></i>
            </div>
            <div className="font-semibold text-sm mb-1" style={{ color: "var(--text-primary)" }}>{item.title}</div>
            <div className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>{item.desc}</div>
          </div>
        ))}
      </div>
      <div>
        <h2 className="text-xl font-bold mb-4" style={{ color: "var(--text-primary)" }}>{t("features.title")}</h2>
        <div className="space-y-3">
          {abilities.map((item, i) => (
            <div key={i} className="flex gap-4 p-4 rounded-xl border" style={{ borderColor: "var(--border-color)", backgroundColor: "var(--card-bg)" }}>
              <div className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0 mt-0.5" style={{ backgroundColor: "var(--accent-light)" }}>
                <i className={`${item.icon} text-sm`} style={{ color: "var(--accent)" }}></i>
              </div>
              <div>
                <div className="font-semibold text-sm mb-1" style={{ color: "var(--text-primary)" }}>{item.title}</div>
                <div className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="p-5 rounded-xl border" style={{ borderColor: "var(--border-color)", backgroundColor: "var(--accent-light)" }}>
        <div className="text-sm font-semibold mb-2" style={{ color: "var(--accent)" }}>{t("features.sdk.title")}</div>
        <div className="flex flex-wrap gap-2">
          {platforms.map((p) => (
            <span key={p} className="px-3 py-1 rounded-full text-xs font-medium border" style={{ borderColor: "var(--accent)", color: "var(--accent)" }}>{p}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

function QuickstartSection() {
  const { t } = useTranslation();
  const copy = t("docs.copy");
  const copied = t("docs.copied");
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-3" style={{ color: "var(--text-primary)" }}>{t("docs.nav.quickstart")}</h1>
        <p className="text-base leading-relaxed" style={{ color: "var(--text-secondary)" }}>
          {t("api.hero.btn.guide")} — TupIM
        </p>
      </div>
      <div className="space-y-6">
        <StepBlock step={1} title={t("docs.install")}>
          <div className="space-y-3">
            <CodeBlock label="Android (Gradle)" copyLabel={copy} copiedLabel={copied}>
              {`// build.gradle\ndependencies {\n    implementation 'io.tupim:sdk-android:2.1.0'\n}`}
            </CodeBlock>
            <CodeBlock label="iOS (CocoaPods)" copyLabel={copy} copiedLabel={copied}>
              {`# Podfile\npod 'TupIMSDK', '~> 2.1.0'`}
            </CodeBlock>
            <CodeBlock label="Web (npm)" copyLabel={copy} copiedLabel={copied}>
              {`npm install @tupim/web-sdk`}
            </CodeBlock>
          </div>
        </StepBlock>
        <StepBlock step={2} title={t("docs.init")}>
          <CodeBlock label="Web SDK" copyLabel={copy} copiedLabel={copied}>
            {`import TupIM from '@tupim/web-sdk';\n\nconst client = new TupIM({\n  serverURL: 'wss://your-server.com/ws',\n  appID: 'your_app_id',\n  appSecret: 'your_app_secret',\n});\n\nawait client.connect();`}
          </CodeBlock>
        </StepBlock>
        <StepBlock step={3} title="Login">
          <CodeBlock label="" copyLabel={copy} copiedLabel={copied}>
            {`await client.login({\n  userID: 'user_123',\n  token: 'your_user_token',\n});\n\nconsole.log('Login:', client.currentUser);`}
          </CodeBlock>
        </StepBlock>
        <StepBlock step={4} title="Send Message">
          <CodeBlock label="" copyLabel={copy} copiedLabel={copied}>
            {`const message = await client.sendMessage({\n  conversationID: 'conv_target_user_456',\n  type: 'text',\n  content: 'Hello, TupIM!',\n});\n\nclient.on('newMessage', (msg) => {\n  console.log('New message:', msg.content);\n});`}
          </CodeBlock>
        </StepBlock>
      </div>
      <div className="p-5 rounded-xl border" style={{ borderColor: "var(--border-color)", backgroundColor: "var(--card-bg)" }}>
        <div className="flex items-start gap-3">
          <div className="w-7 h-7 flex items-center justify-center rounded-full shrink-0 mt-0.5" style={{ backgroundColor: "var(--accent-light)" }}>
            <i className="ri-lightbulb-line text-sm" style={{ color: "var(--accent)" }}></i>
          </div>
          <div>
            <div className="text-sm font-semibold mb-1" style={{ color: "var(--text-primary)" }}>Next Steps</div>
            <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              {t("api.hero.desc")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function MessagingSection() {
  const { t } = useTranslation();
  const copy = t("docs.copy");
  const copied = t("docs.copied");
  const msgTypes = [
    { icon: "ri-text", title: "Text", desc: "Unicode full charset, Emoji, up to 10,000 chars" },
    { icon: "ri-image-line", title: "Image", desc: "JPG / PNG / WebP, auto thumbnail, original view" },
    { icon: "ri-mic-line", title: "Voice", desc: "Up to 60s, optional speech-to-text, waveform display" },
    { icon: "ri-video-line", title: "Video", desc: "Auto cover screenshot, streaming playback" },
    { icon: "ri-file-line", title: "File", desc: "Any format up to 2GB, resume support, progress display" },
    { icon: "ri-map-pin-line", title: "Location", desc: "Real-time or static location with map preview" },
    { icon: "ri-link", title: "Link Card", desc: "Auto-parsed rich preview card with title & thumbnail" },
    { icon: "ri-code-box-line", title: "Custom", desc: "Extend with custom JSON message types" },
  ];
  const statuses = [
    { icon: "ri-check-double-line", title: t("showcase.msg.p2"), desc: t("showcase.msg.p2") },
    { icon: "ri-delete-back-2-line", title: t("showcase.msg.p3"), desc: t("showcase.msg.p3") },
    { icon: "ri-reply-line", title: t("showcase.msg.p3"), desc: t("showcase.msg.p3") },
    { icon: "ri-at-line", title: "@Mention", desc: t("showcase.msg.p3") },
    { icon: "ri-emotion-line", title: "Reactions", desc: "Add emoji reactions to messages" },
    { icon: "ri-time-line", title: t("showcase.msg.p4"), desc: t("showcase.msg.p4") },
  ];
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-3" style={{ color: "var(--text-primary)" }}>{t("docs.nav.messaging")}</h1>
        <p className="text-base leading-relaxed" style={{ color: "var(--text-secondary)" }}>{t("feat.msg.desc")}</p>
      </div>
      <FeatureList title={t("showcase.msg.desc")} items={msgTypes} />
      <div>
        <h2 className="text-xl font-bold mb-4" style={{ color: "var(--text-primary)" }}>{t("showcase.msg.tag")}</h2>
        <div className="space-y-3">
          {statuses.map((item, i) => (
            <div key={i} className="flex gap-4 p-4 rounded-xl border" style={{ borderColor: "var(--border-color)", backgroundColor: "var(--card-bg)" }}>
              <div className="w-8 h-8 flex items-center justify-center rounded-lg shrink-0 mt-0.5" style={{ backgroundColor: "var(--accent-light)" }}>
                <i className={`${item.icon} text-sm`} style={{ color: "var(--accent)" }}></i>
              </div>
              <div className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{item.desc}</div>
            </div>
          ))}
        </div>
      </div>
      <CodeBlock label="Send Messages" copyLabel={copy} copiedLabel={copied}>
        {`await client.sendTextMessage('conv_id', 'Hello World');\n\nconst imageMsg = await client.sendImageMessage('conv_id', {\n  file: imageFile,\n  compress: true,\n});\n\nclient.on('newMessage', (message) => {\n  client.markMessageRead(message.conversationID, message.messageID);\n});`}
      </CodeBlock>
    </div>
  );
}

function CallsSection() {
  const { t } = useTranslation();
  const copy = t("docs.copy");
  const copied = t("docs.copied");
  const callFeatures = [
    { icon: "ri-phone-line", title: t("showcase.call.p1"), desc: t("showcase.call.p1") },
    { icon: "ri-vidicon-line", title: "1080P HD Video", desc: "Camera switch, beauty filter" },
    { icon: "ri-group-2-line", title: t("showcase.call.p1"), desc: t("showcase.call.p1") },
    { icon: "ri-computer-line", title: t("showcase.call.p2"), desc: t("showcase.call.p2") },
    { icon: "ri-record-circle-line", title: t("showcase.call.p4"), desc: t("showcase.call.p4") },
    { icon: "ri-wifi-line", title: t("showcase.call.p3"), desc: t("showcase.call.p3") },
  ];
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-3" style={{ color: "var(--text-primary)" }}>{t("docs.nav.calls")}</h1>
        <p className="text-base leading-relaxed" style={{ color: "var(--text-secondary)" }}>{t("feat.call.desc")}</p>
      </div>
      <FeatureList title={t("showcase.call.desc")} items={callFeatures} />
      <div className="space-y-4">
        <CodeBlock label="1-on-1 Video Call" copyLabel={copy} copiedLabel={copied}>
          {`const call = await client.startVideoCall({\n  targetUserID: 'user_456',\n  enableCamera: true,\n  enableMicrophone: true,\n});\n\ncall.on('accepted', () => console.log('Accepted'));\ncall.on('ended', () => console.log('Ended'));\n\nawait call.hangup();`}
        </CodeBlock>
        <CodeBlock label="Group Call" copyLabel={copy} copiedLabel={copied}>
          {`const room = await client.createGroupCall({\n  groupID: 'group_123',\n  type: 'video',\n});\n\nawait room.join({ enableCamera: true, enableMic: true });\nawait room.startScreenShare();\nawait room.startRecording({ storagePath: '/recordings' });`}
        </CodeBlock>
      </div>
    </div>
  );
}

function GroupsSection() {
  const { t } = useTranslation();
  const copy = t("docs.copy");
  const copied = t("docs.copied");
  const isZh = t("docs.nav.groups") === "群組管理";
  const groupCards = [
    { icon: "ri-group-line", title: isZh ? "成員管理" : "Member Management", items: [t("showcase.group.p1"), t("showcase.group.p4"), t("showcase.group.p2"), t("showcase.group.p3")] },
    { icon: "ri-shield-user-line", title: isZh ? "角色權限" : "Roles & Permissions", items: [isZh ? "群主 / 管理員 / 普通成員" : "Owner / Admin / Member", t("showcase.group.p2"), t("showcase.group.p3"), t("showcase.group.p4")] },
    { icon: "ri-megaphone-line", title: isZh ? "群組公告" : "Announcements", items: [t("showcase.group.p3"), t("showcase.group.p4"), t("showcase.group.p1"), t("showcase.group.p2")] },
    { icon: "ri-folder-line", title: isZh ? "群組資源" : "Group Resources", items: [t("showcase.group.p3"), t("showcase.group.p4"), t("showcase.group.p1"), t("showcase.group.p2")] },
  ];
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-3" style={{ color: "var(--text-primary)" }}>{t("docs.nav.groups")}</h1>
        <p className="text-base leading-relaxed" style={{ color: "var(--text-secondary)" }}>{t("feat.group.desc")}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {groupCards.map((card) => (
          <div key={card.title} className="p-5 rounded-xl border" style={{ borderColor: "var(--border-color)", backgroundColor: "var(--card-bg)" }}>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: "var(--accent-light)" }}>
                <i className={`${card.icon} text-sm`} style={{ color: "var(--accent)" }}></i>
              </div>
              <span className="font-semibold text-sm" style={{ color: "var(--text-primary)" }}>{card.title}</span>
            </div>
            <ul className="space-y-2">
              {card.items.map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-sm" style={{ color: "var(--text-secondary)" }}>
                  <i className="ri-check-line text-xs" style={{ color: "var(--accent)" }}></i>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <CodeBlock label="Group Operations" copyLabel={copy} copiedLabel={copied}>
        {`const group = await client.createGroup({\n  name: 'Dev Team',\n  memberIDs: ['user_a', 'user_b'],\n  joinType: 'apply',\n});\n\nawait client.setGroupAdmin(group.groupID, 'user_a');\nawait client.inviteToGroup(group.groupID, ['user_d']);\nawait client.muteGroupMember(group.groupID, 'user_b', { duration: 86400 });`}
      </CodeBlock>
    </div>
  );
}

function EncryptionSection() {
  const { t } = useTranslation();
  const copy = t("docs.copy");
  const copied = t("docs.copied");
  const secFeatures = [
    { icon: "ri-key-line", title: "Signal Protocol", desc: t("showcase.security.p1") },
    { icon: "ri-fire-line", title: t("showcase.security.p2"), desc: t("showcase.security.p2") },
    { icon: "ri-server-line", title: t("showcase.security.p3"), desc: t("showcase.security.p3") },
    { icon: "ri-eye-off-line", title: "Zero Knowledge", desc: t("showcase.security.p1") },
    { icon: "ri-file-shield-line", title: t("showcase.security.p4"), desc: t("showcase.security.p4") },
    { icon: "ri-device-line", title: "Device Management", desc: t("showcase.security.p4") },
  ];
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-3" style={{ color: "var(--text-primary)" }}>{t("docs.nav.encryption")}</h1>
        <p className="text-base leading-relaxed" style={{ color: "var(--text-secondary)" }}>{t("feat.encrypt.desc")}</p>
      </div>
      <div className="p-5 rounded-xl border" style={{ borderColor: "var(--border-color)", backgroundColor: "var(--accent-light)" }}>
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-full shrink-0" style={{ backgroundColor: "var(--accent)" }}>
            <i className="ri-shield-check-line text-sm text-white"></i>
          </div>
          <div>
            <div className="text-sm font-bold mb-1" style={{ color: "var(--text-primary)" }}>{t("showcase.security.tag")}</div>
            <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{t("showcase.security.desc")}</p>
          </div>
        </div>
      </div>
      <FeatureList title={t("showcase.security.title")} items={secFeatures} />
      <CodeBlock label="Encryption & Privacy" copyLabel={copy} copiedLabel={copied}>
        {`await client.setEncryptionEnabled(true);\n\nawait client.sendMessage('conv_id', {\n  type: 'text',\n  content: 'This message auto-destructs in 10s',\n  burnAfterRead: true,\n  burnDuration: 10,\n});\n\nconst devices = await client.getLoginDevices();\nawait client.logoutDevice(device.deviceID);`}
      </CodeBlock>
    </div>
  );
}

function SdkSection() {
  const { t } = useTranslation();
  const copy = t("docs.copy");
  const copied = t("docs.copied");
  const installLabel = t("docs.install");
  const initLabel = t("docs.init");
  const sdks = [
    {
      platform: "Android SDK",
      icon: "ri-android-line",
      install: `// build.gradle (app)\ndependencies {\n    implementation 'io.tupim:sdk-android:2.1.0'\n}`,
      init: `class MyApp : Application() {\n    override fun onCreate() {\n        super.onCreate()\n        TupIM.init(this, TupIMConfig(\n            serverURL = "wss://your-server.com/ws",\n            appID = "your_app_id",\n        ))\n    }\n}`,
    },
    {
      platform: "iOS SDK",
      icon: "ri-apple-line",
      install: `# Podfile\npod 'TupIMSDK', '~> 2.1.0'\n\n# Swift Package Manager\n.package(url: "https://github.com/tupim/tupim-ios-sdk", from: "2.1.0")`,
      init: `TupIM.initialize(config: TupIMConfig(\n    serverURL: "wss://your-server.com/ws",\n    appID: "your_app_id"\n))\n\nTupIM.login(userID: "user_123", token: token) { result in\n    print(result)\n}`,
    },
    {
      platform: "Web SDK",
      icon: "ri-global-line",
      install: `npm install @tupim/web-sdk\n# or\nyarn add @tupim/web-sdk`,
      init: `import TupIM from '@tupim/web-sdk';\n\nconst client = new TupIM({\n  serverURL: 'wss://your-server.com/ws',\n  appID: 'your_app_id',\n});\n\nawait client.connect();\nawait client.login({ userID: 'user_123', token: 'user_token' });`,
    },
  ];
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-3" style={{ color: "var(--text-primary)" }}>{t("docs.nav.sdk")}</h1>
        <p className="text-base leading-relaxed" style={{ color: "var(--text-secondary)" }}>{t("features.sdk.desc")}</p>
      </div>
      <div className="space-y-6">
        {sdks.map((sdk) => (
          <div key={sdk.platform} className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--border-color)" }}>
            <div className="flex items-center gap-3 px-5 py-4 border-b" style={{ borderColor: "var(--border-color)", backgroundColor: "var(--card-bg)" }}>
              <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: "var(--accent-light)" }}>
                <i className={`${sdk.icon} text-sm`} style={{ color: "var(--accent)" }}></i>
              </div>
              <span className="font-semibold text-sm" style={{ color: "var(--text-primary)" }}>{sdk.platform}</span>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <div className="text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: "var(--text-secondary)" }}>{installLabel}</div>
                <pre className="text-xs leading-relaxed p-4 rounded-lg overflow-x-auto" style={{ backgroundColor: "var(--code-bg)", color: "var(--code-text)" }}>
                  <code>{sdk.install}</code>
                </pre>
              </div>
              <div>
                <div className="text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: "var(--text-secondary)" }}>{initLabel}</div>
                <pre className="text-xs leading-relaxed p-4 rounded-lg overflow-x-auto" style={{ backgroundColor: "var(--code-bg)", color: "var(--code-text)" }}>
                  <code>{sdk.init}</code>
                </pre>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DeploymentSection() {
  const { t } = useTranslation();
  const copy = t("docs.copy");
  const copied = t("docs.copied");
  const isZh = t("docs.nav.deployment") === "私有化部署";
  const configs = [
    { title: isZh ? "最低配置（開發/測試）" : "Minimum (Dev/Test)", items: ["CPU: 2 cores", "RAM: 4 GB", "Disk: 20 GB SSD", "OS: Ubuntu 20.04+"] },
    { title: isZh ? "推薦配置（生產環境）" : "Recommended (Production)", items: ["CPU: 4+ cores", "RAM: 8+ GB", "Disk: 100 GB SSD", "OS: Ubuntu 22.04 LTS"] },
  ];
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-3" style={{ color: "var(--text-primary)" }}>{t("docs.nav.deployment")}</h1>
        <p className="text-base leading-relaxed" style={{ color: "var(--text-secondary)" }}>{t("api.deploy.desc")}</p>
      </div>
      <div>
        <h2 className="text-xl font-bold mb-4" style={{ color: "var(--text-primary)" }}>{isZh ? "系統要求" : "System Requirements"}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {configs.map((card) => (
            <div key={card.title} className="p-4 rounded-xl border" style={{ borderColor: "var(--border-color)", backgroundColor: "var(--card-bg)" }}>
              <div className="font-semibold text-sm mb-3" style={{ color: "var(--text-primary)" }}>{card.title}</div>
              <ul className="space-y-1.5">
                {card.items.map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm" style={{ color: "var(--text-secondary)" }}>
                    <i className="ri-server-line text-xs" style={{ color: "var(--accent)" }}></i>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h2 className="text-xl font-bold mb-4" style={{ color: "var(--text-primary)" }}>Docker Compose</h2>
        <div className="space-y-4">
          <StepBlock step={1} title={isZh ? "下載配置文件" : "Clone Config"}>
            <CodeBlock label="" copyLabel={copy} copiedLabel={copied}>
              {`git clone https://github.com/tupim/tupim-deploy.git\ncd tupim-deploy`}
            </CodeBlock>
          </StepBlock>
          <StepBlock step={2} title={isZh ? "編輯配置" : "Edit Config"}>
            <CodeBlock label="docker-compose.yml" copyLabel={copy} copiedLabel={copied}>
              {`version: '3.8'\nservices:\n  tupim-server:\n    image: tupim/server:2.1.0\n    environment:\n      - APP_ID=your_app_id\n      - APP_SECRET=your_secret\n      - DB_HOST=mysql\n    ports:\n      - "8080:8080"\n      - "8081:8081"\n    depends_on:\n      - mysql\n      - redis`}
            </CodeBlock>
          </StepBlock>
          <StepBlock step={3} title={isZh ? "啟動服務" : "Start Services"}>
            <CodeBlock label="" copyLabel={copy} copiedLabel={copied}>
              {`docker-compose up -d\ndocker-compose ps\ndocker-compose logs -f tupim-server`}
            </CodeBlock>
          </StepBlock>
        </div>
      </div>
    </div>
  );
}

function ApiSection() {
  const { t } = useTranslation();
  const copy = t("docs.copy");
  const copied = t("docs.copied");
  const isZh = t("docs.nav.api") === "API 參考";
  const endpoints = [
    {
      method: "POST", path: "/messages/send", desc: isZh ? "發送消息" : "Send Message", color: "#22c55e",
      body: `{\n  "conversationID": "conv_123",\n  "type": "text",\n  "content": "Hello, TupIM!"\n}`,
      response: `{\n  "code": 0,\n  "data": {\n    "messageID": "msg_abc123",\n    "status": "sent"\n  }\n}`,
    },
    {
      method: "GET", path: "/messages/history", desc: isZh ? "獲取歷史消息" : "Get History", color: "#3b82f6",
      body: `conversationID=conv_123&limit=20`,
      response: `{\n  "code": 0,\n  "data": {\n    "messages": [...],\n    "hasMore": true\n  }\n}`,
    },
    {
      method: "POST", path: "/groups/create", desc: isZh ? "創建群組" : "Create Group", color: "#22c55e",
      body: `{\n  "name": "Dev Team",\n  "memberIDs": ["user_a"],\n  "joinType": "apply"\n}`,
      response: `{\n  "code": 0,\n  "data": {\n    "groupID": "group_xyz"\n  }\n}`,
    },
    {
      method: "DELETE", path: "/messages/{messageID}", desc: isZh ? "撤回消息" : "Recall Message", color: "#ef4444",
      body: `// Path Param: messageID`,
      response: `{\n  "code": 0\n}`,
    },
  ];
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-3" style={{ color: "var(--text-primary)" }}>{t("docs.nav.api")}</h1>
        <p className="text-base leading-relaxed" style={{ color: "var(--text-secondary)" }}>{t("api.deploy.btn")}</p>
      </div>
      <div className="p-4 rounded-xl border" style={{ borderColor: "var(--border-color)", backgroundColor: "var(--card-bg)" }}>
        <div className="text-sm font-semibold mb-2" style={{ color: "var(--text-primary)" }}>Base URL</div>
        <code className="text-sm px-3 py-1.5 rounded-lg" style={{ backgroundColor: "var(--code-bg)", color: "var(--accent)" }}>
          https://your-server.com/v1/api
        </code>
      </div>
      <div>
        <h2 className="text-xl font-bold mb-3" style={{ color: "var(--text-primary)" }}>Authentication</h2>
        <CodeBlock label="Request Header" copyLabel={copy} copiedLabel={copied}>
          {`Authorization: Bearer <your_jwt_token>\nContent-Type: application/json`}
        </CodeBlock>
      </div>
      <div className="space-y-4">
        {endpoints.map((api) => (
          <div key={api.path} className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--border-color)" }}>
            <div className="flex items-center gap-3 px-5 py-3 border-b" style={{ borderColor: "var(--border-color)", backgroundColor: "var(--card-bg)" }}>
              <span className="text-xs font-bold px-2 py-0.5 rounded" style={{ backgroundColor: api.color + "20", color: api.color }}>{api.method}</span>
              <code className="text-sm font-mono" style={{ color: "var(--text-primary)" }}>{api.path}</code>
              <span className="text-xs ml-auto" style={{ color: "var(--text-secondary)" }}>{api.desc}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="p-4 border-r" style={{ borderColor: "var(--border-color)" }}>
                <div className="text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: "var(--text-secondary)" }}>Request</div>
                <pre className="text-xs leading-relaxed p-3 rounded-lg overflow-x-auto" style={{ backgroundColor: "var(--code-bg)", color: "var(--code-text)" }}>
                  <code>{api.body}</code>
                </pre>
              </div>
              <div className="p-4">
                <div className="text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: "var(--text-secondary)" }}>Response</div>
                <pre className="text-xs leading-relaxed p-3 rounded-lg overflow-x-auto" style={{ backgroundColor: "var(--code-bg)", color: "var(--code-text)" }}>
                  <code>{api.response}</code>
                </pre>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SfuSection() {
  const { t } = useTranslation();

  const examples = [
    { n: 2,  up: 3.0,  down: 1.2,  total: 4.2 },
    { n: 4,  up: 6.0,  down: 14.4, total: 20.4 },
    { n: 6,  up: 9.0,  down: 36.0, total: 45.0 },
    { n: 10, up: 15.0, down: 108.0, total: 123.0 },
    { n: 20, up: 30.0, down: 456.0, total: 486.0 },
  ];

  const notes = [
    t("docs.sfu.note.1"),
    t("docs.sfu.note.2"),
    t("docs.sfu.note.3"),
    t("docs.sfu.note.4"),
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold mb-3" style={{ color: "var(--text-primary)" }}>
          {t("docs.sfu.title")}
        </h1>
        <p className="text-base leading-relaxed" style={{ color: "var(--text-secondary)" }}>
          {t("docs.sfu.intro")}
        </p>
      </div>

      {/* Architecture Overview */}
      <div>
        <h2 className="text-xl font-bold mb-3" style={{ color: "var(--text-primary)" }}>
          {t("docs.sfu.arch.title")}
        </h2>
        <p className="text-sm leading-relaxed mb-4" style={{ color: "var(--text-secondary)" }}>
          {t("docs.sfu.arch.desc")}
        </p>
        {/* Image 1 — Architecture diagram */}
        <div className="w-full rounded-xl overflow-hidden border" style={{ borderColor: "var(--border-color)" }}>
          <img
            src="https://storage.readdy-site.link/project_files/113546dc-a8c1-44fa-8aac-7a04c0cd68d4/7a4a8ced-041f-46ea-8594-e65a32b7e8ed_31861779184748_.pic.jpg?v=54d1b2cd9ee5a702154b857f1ca4936e"
            alt="SFU Architecture Diagram"
            className="w-full object-top"
            style={{ maxHeight: 420, objectFit: "contain", backgroundColor: "var(--card-bg)" }}
          />
        </div>
      </div>

      {/* Formula */}
      <div>
        <h2 className="text-xl font-bold mb-3" style={{ color: "var(--text-primary)" }}>
          {t("docs.sfu.formula.title")}
        </h2>
        <div className="space-y-2 mb-4">
          {[
            t("docs.sfu.formula.uplink"),
            t("docs.sfu.formula.downlink"),
          ].map((line, i) => (
            <div key={i} className="flex items-start gap-2 text-sm" style={{ color: "var(--text-secondary)" }}>
              <i className="ri-arrow-right-s-line mt-0.5 shrink-0" style={{ color: "var(--accent)" }}></i>
              {line}
            </div>
          ))}
        </div>
        <div
          className="p-4 rounded-xl border font-mono text-sm"
          style={{ borderColor: "var(--accent)", backgroundColor: "var(--accent-light)", color: "var(--accent)" }}
        >
          {t("docs.sfu.formula.total")}
        </div>
      </div>

      {/* Bitrate Params */}
      <div>
        <h2 className="text-xl font-bold mb-3" style={{ color: "var(--text-primary)" }}>
          {t("docs.sfu.params.title")}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { label: "HD (720p)", up: "1.5 Mbps", down: "1.2 Mbps", icon: "ri-hd-line", key: t("docs.sfu.params.hd") },
            { label: "SD (480p)", up: "0.8 Mbps", down: "0.6 Mbps", icon: "ri-sd-card-line", key: t("docs.sfu.params.sd") },
            { label: "Low (240p)", up: "0.3 Mbps", down: "0.25 Mbps", icon: "ri-wifi-line", key: t("docs.sfu.params.ld") },
          ].map((item) => (
            <div key={item.label} className="p-4 rounded-xl border" style={{ borderColor: "var(--border-color)", backgroundColor: "var(--card-bg)" }}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ backgroundColor: "var(--accent-light)" }}>
                  <i className={`${item.icon} text-sm`} style={{ color: "var(--accent)" }}></i>
                </div>
                <span className="font-semibold text-sm" style={{ color: "var(--text-primary)" }}>{item.label}</span>
              </div>
              <div className="space-y-1">
                <div className="flex justify-between text-xs" style={{ color: "var(--text-secondary)" }}>
                  <span>Upload</span>
                  <span className="font-medium" style={{ color: "var(--accent)" }}>{item.up}</span>
                </div>
                <div className="flex justify-between text-xs" style={{ color: "var(--text-secondary)" }}>
                  <span>Download</span>
                  <span className="font-medium" style={{ color: "var(--accent)" }}>{item.down}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Calculation Examples Table */}
      <div>
        <h2 className="text-xl font-bold mb-2" style={{ color: "var(--text-primary)" }}>
          {t("docs.sfu.example.title")}
        </h2>
        <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>{t("docs.sfu.example.desc")}</p>
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--border-color)" }}>
          <div
            className="grid grid-cols-4 text-xs font-semibold px-4 py-2.5"
            style={{ backgroundColor: "var(--card-bg)", color: "var(--text-secondary)", borderBottom: "1px solid var(--border-color)" }}
          >
            <span>Participants (N)</span>
            <span>Uplink (Mbps)</span>
            <span>Downlink (Mbps)</span>
            <span>Total (Mbps)</span>
          </div>
          {examples.map((row, i) => (
            <div
              key={row.n}
              className="grid grid-cols-4 text-sm px-4 py-3"
              style={{
                backgroundColor: i % 2 === 0 ? "transparent" : "var(--card-bg)",
                borderBottom: i < examples.length - 1 ? "1px solid var(--border-color)" : undefined,
                color: "var(--text-primary)",
              }}
            >
              <span className="font-semibold" style={{ color: "var(--accent)" }}>{row.n}</span>
              <span>{row.up.toFixed(1)}</span>
              <span>{row.down.toFixed(1)}</span>
              <span className="font-semibold">{row.total.toFixed(1)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Image 2 — detailed chart / infographic */}
      <div>
        <div className="w-full rounded-xl overflow-hidden border" style={{ borderColor: "var(--border-color)" }}>
          <img
            src="https://storage.readdy-site.link/project_files/113546dc-a8c1-44fa-8aac-7a04c0cd68d4/040f6909-f435-4bbf-8328-6f49ab5d7246_31871779184750_.pic_hd.jpg?v=bbb763ffe09cf59ab629a7d7c1b0b452"
            alt="SFU Bandwidth Calculation Chart"
            className="w-full object-top"
            style={{ maxHeight: 480, objectFit: "contain", backgroundColor: "var(--card-bg)" }}
          />
        </div>
      </div>

      {/* Notes */}
      <div className="p-5 rounded-xl border" style={{ borderColor: "var(--border-color)", backgroundColor: "var(--card-bg)" }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 flex items-center justify-center rounded-full" style={{ backgroundColor: "var(--accent-light)" }}>
            <i className="ri-information-line text-xs" style={{ color: "var(--accent)" }}></i>
          </div>
          <span className="font-semibold text-sm" style={{ color: "var(--text-primary)" }}>{t("docs.sfu.note.title")}</span>
        </div>
        <ul className="space-y-2">
          {notes.map((note, i) => (
            <li key={i} className="flex items-start gap-2 text-sm" style={{ color: "var(--text-secondary)" }}>
              <i className="ri-checkbox-circle-line mt-0.5 shrink-0" style={{ color: "var(--accent)" }}></i>
              {note}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ---- Section map ----
const SECTION_COMPONENTS: Record<SectionKey, () => JSX.Element> = {
  introduction: IntroductionSection,
  quickstart: QuickstartSection,
  messaging: MessagingSection,
  calls: CallsSection,
  groups: GroupsSection,
  encryption: EncryptionSection,
  sdk: SdkSection,
  deployment: DeploymentSection,
  api: ApiSection,
  sfu: SfuSection,
};

// ---- Main Page ----
export default function DocsPage() {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState<SectionKey>("introduction");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Build nav items using t() so they re-render on language switch
  const navItems = [
    { key: "introduction" as SectionKey, label: t("docs.nav.introduction"), icon: "ri-book-open-line", group: t("docs.group.start") },
    { key: "quickstart" as SectionKey, label: t("docs.nav.quickstart"), icon: "ri-rocket-line", group: t("docs.group.start") },
    { key: "messaging" as SectionKey, label: t("docs.nav.messaging"), icon: "ri-chat-1-line", group: t("docs.group.core") },
    { key: "calls" as SectionKey, label: t("docs.nav.calls"), icon: "ri-video-line", group: t("docs.group.core") },
    { key: "groups" as SectionKey, label: t("docs.nav.groups"), icon: "ri-group-line", group: t("docs.group.core") },
    { key: "encryption" as SectionKey, label: t("docs.nav.encryption"), icon: "ri-shield-keyhole-line", group: t("docs.group.core") },
    { key: "sdk" as SectionKey, label: t("docs.nav.sdk"), icon: "ri-code-s-slash-line", group: t("docs.group.dev") },
    { key: "deployment" as SectionKey, label: t("docs.nav.deployment"), icon: "ri-server-line", group: t("docs.group.dev") },
    { key: "api" as SectionKey, label: t("docs.nav.api"), icon: "ri-terminal-box-line", group: t("docs.group.dev") },
    { key: "sfu" as SectionKey, label: t("docs.nav.sfu"), icon: "ri-broadcast-line", group: t("docs.group.server") },
  ];

  const groups = [t("docs.group.start"), t("docs.group.core"), t("docs.group.dev"), t("docs.group.server")];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeSection]);

  const CurrentSection = SECTION_COMPONENTS[activeSection];
  const currentLabel = navItems.find((n) => n.key === activeSection)?.label ?? "";

  const renderNavItems = (groupName: string) =>
    navItems
      .filter((n) => n.group === groupName)
      .map((item) => (
        <button
          key={item.key}
          onClick={() => { setActiveSection(item.key); setMobileNavOpen(false); }}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-left transition-colors cursor-pointer whitespace-nowrap"
          style={
            activeSection === item.key
              ? { backgroundColor: "var(--accent-light)", color: "var(--accent)", fontWeight: 600 }
              : { color: "var(--text-secondary)" }
          }
        >
          <i className={`${item.icon} text-base`}></i>
          {item.label}
        </button>
      ));

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--bg-primary)" }}>
      <Navbar />
      <div className="pt-16 flex">
        {/* Sidebar - Desktop */}
        <aside
          className="hidden lg:flex flex-col w-60 shrink-0 fixed top-16 bottom-0 overflow-y-auto border-r"
          style={{ borderColor: "var(--border-color)", backgroundColor: "var(--sidebar-bg, var(--bg-primary))" }}
        >
          <div className="p-4">
            <div className="flex items-center gap-2 px-3 py-2 mb-3">
              <i className="ri-book-2-line" style={{ color: "var(--accent)" }}></i>
              <span className="font-bold text-sm" style={{ color: "var(--text-primary)" }}>{t("docs.title")}</span>
            </div>
            {groups.map((group) => (
              <div key={group} className="mb-4">
                <div className="px-3 py-1 text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: "var(--text-secondary)" }}>{group}</div>
                {renderNavItems(group)}
              </div>
            ))}
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 lg:ml-60 min-w-0">
          {/* Mobile Nav Toggle */}
          <div className="lg:hidden flex items-center gap-3 px-4 py-3 border-b sticky top-16 z-40" style={{ borderColor: "var(--border-color)", backgroundColor: "var(--bg-primary)" }}>
            <button
              onClick={() => setMobileNavOpen(!mobileNavOpen)}
              className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg border cursor-pointer"
              style={{ borderColor: "var(--border-color)", color: "var(--text-primary)" }}
            >
              <i className="ri-menu-line"></i>
              {t("docs.toc")}
            </button>
            <span className="text-sm" style={{ color: "var(--text-secondary)" }}>{currentLabel}</span>
          </div>

          {mobileNavOpen && (
            <div className="lg:hidden border-b px-4 py-3" style={{ borderColor: "var(--border-color)", backgroundColor: "var(--bg-primary)" }}>
              {groups.map((group) => (
                <div key={group} className="mb-3">
                  <div className="text-xs font-semibold uppercase tracking-wide mb-1 px-3" style={{ color: "var(--text-secondary)" }}>{group}</div>
                  {renderNavItems(group)}
                </div>
              ))}
            </div>
          )}

          <div className="max-w-3xl mx-auto px-6 py-10">
            <CurrentSection />

            {/* Prev / Next */}
            <div className="mt-12 pt-8 border-t flex items-center justify-between" style={{ borderColor: "var(--border-color)" }}>
              {(() => {
                const idx = navItems.findIndex((n) => n.key === activeSection);
                const prev = navItems[idx - 1];
                const next = navItems[idx + 1];
                return (
                  <>
                    {prev ? (
                      <button
                        onClick={() => setActiveSection(prev.key)}
                        className="flex items-center gap-2 text-sm cursor-pointer transition-colors"
                        style={{ color: "var(--text-secondary)" }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = "var(--accent)")}
                        onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-secondary)")}
                      >
                        <i className="ri-arrow-left-line"></i>
                        {prev.label}
                      </button>
                    ) : <div />}
                    {next ? (
                      <button
                        onClick={() => setActiveSection(next.key)}
                        className="flex items-center gap-2 text-sm cursor-pointer transition-colors"
                        style={{ color: "var(--text-secondary)" }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = "var(--accent)")}
                        onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-secondary)")}
                      >
                        {next.label}
                        <i className="ri-arrow-right-line"></i>
                      </button>
                    ) : <div />}
                  </>
                );
              })()}
            </div>
          </div>
        </main>
      </div>

      <div className="lg:ml-60">
        <Footer />
      </div>
    </div>
  );
}